import { safeDate } from "@/lib/safe-date";
import { trackGaEvent } from "@/lib/ga";

export type PlanDayInput = {
  date: string;
  sections: { name: string; hours: number }[];
  hours: number;
  startTime?: string;
  endTime?: string;
};

export type WeekDayRow = {
  index: number | null;
  isoDate: string;
  weekday: string;
  weekdayShort: string;
  topic: string;
  hours: number;
  isRest: boolean;
  timeLabel?: string;
};

export type WeekGroup = {
  id: string;
  weekNumber: number;
  weekStartIso: string;
  weekEndIso: string;
  rangeLabel: string;
  hours: number;
  days: WeekDayRow[];
};

export type MonthGroup = {
  key: string;
  label: string;
  hours: number;
  weeks: WeekGroup[];
};

const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function toIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addDays(d: Date, n: number): Date {
  const next = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  next.setDate(next.getDate() + n);
  return next;
}

function weekStartSunday(d: Date): Date {
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  start.setDate(start.getDate() - start.getDay());
  return start;
}

function formatRange(start: Date, end: Date): string {
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  return `${start.toLocaleDateString("en-US", opts)} – ${end.toLocaleDateString("en-US", opts)}`;
}

function icsEscape(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

function icsStamp(d = new Date()): string {
  return d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
}

function parseHm(value: string | undefined): { h: number; m: number } | null {
  if (!value) return null;
  const match = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(value.trim());
  if (!match) return null;
  return { h: Number(match[1]), m: Number(match[2]) };
}

/** Add whole hours to an HH:MM clock time, wrapping at midnight. */
export function addHoursToTime(startTime: string, hours: number): string {
  const parsed = parseHm(startTime);
  if (!parsed) return startTime;
  const total = (((parsed.h * 60 + parsed.m + Math.round(hours * 60)) % 1440) + 1440) % 1440;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export type ClockParts = {
  hour12: number;
  minute: number;
  period: "AM" | "PM";
};

export function splitClock(value?: string | null): ClockParts {
  const parsed = parseHm(value || "") ?? { h: 18, m: 0 };
  return {
    hour12: parsed.h % 12 === 0 ? 12 : parsed.h % 12,
    minute: parsed.m,
    period: parsed.h >= 12 ? "PM" : "AM",
  };
}

export function joinClock(parts: ClockParts): string {
  let h = parts.hour12 % 12;
  if (parts.period === "PM") h += 12;
  return `${String(h).padStart(2, "0")}:${String(parts.minute).padStart(2, "0")}`;
}

export function formatClock(value?: string | null): string {
  const parsed = parseHm(value || "");
  if (!parsed) return "";
  return new Date(2000, 0, 1, parsed.h, parsed.m).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatClockRange(startTime?: string, endTime?: string): string {
  const start = formatClock(startTime);
  const end = formatClock(endTime);
  if (!start || !end) return "";
  return `${start} – ${end}`;
}

function combineLocalDateTime(isoDate: string, hhmm: string): Date | null {
  const d = safeDate(isoDate);
  const hm = parseHm(hhmm);
  if (!d || !hm) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), hm.h, hm.m, 0);
}

/** Group study days into Sunday–Saturday weeks, nested under calendar months. */
export function groupPlanByMonth(plan: PlanDayInput[]): MonthGroup[] {
  const dated = plan
    .map((day, index) => ({ day, index, d: safeDate(day.date) }))
    .filter((row): row is { day: PlanDayInput; index: number; d: Date } => row.d != null);

  if (dated.length === 0) return [];

  const byIso = new Map(dated.map((row) => [toIso(row.d), row]));
  const first = dated[0].d;
  const last = dated[dated.length - 1].d;
  const planStart = weekStartSunday(first);
  const planEnd = addDays(weekStartSunday(last), 6);

  const weeks: WeekGroup[] = [];
  let cursor = planStart;
  let weekNumber = 1;

  while (cursor <= planEnd) {
    const weekEnd = addDays(cursor, 6);
    const days: WeekDayRow[] = [];
    let hours = 0;

    for (let i = 0; i < 7; i++) {
      const date = addDays(cursor, i);
      const iso = toIso(date);
      const match = byIso.get(iso);
      const dow = date.getDay();

      if (match && match.day.hours > 0) {
        hours += match.day.hours;
        days.push({
          index: match.index,
          isoDate: iso,
          weekday: date.toLocaleDateString("en-US", { weekday: "long" }),
          weekdayShort: WEEKDAY_SHORT[dow],
          topic: match.day.sections.map((s) => s.name).join(", ") || "Study",
          hours: match.day.hours,
          isRest: false,
          timeLabel: formatClockRange(match.day.startTime, match.day.endTime),
        });
      }
    }

    if (days.length > 0) {
      weeks.push({
        id: toIso(cursor),
        weekNumber,
        weekStartIso: toIso(cursor),
        weekEndIso: toIso(weekEnd),
        rangeLabel: formatRange(cursor, weekEnd),
        hours,
        days,
      });
      weekNumber += 1;
    }

    cursor = addDays(cursor, 7);
  }

  const months: MonthGroup[] = [];
  for (const week of weeks) {
    const start = safeDate(week.weekStartIso);
    if (!start) continue;
    const key = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}`;
    const label = start.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    let month = months.find((m) => m.key === key);
    if (!month) {
      month = { key, label, hours: 0, weeks: [] };
      months.push(month);
    }
    month.weeks.push(week);
    month.hours += week.hours;
  }

  return months;
}

function icsAllDayEvent(opts: {
  uid: string;
  startIso: string;
  endIso: string;
  summary: string;
  description: string;
  alarmTrigger: string;
  alarmText: string;
}): string {
  return [
    "BEGIN:VEVENT",
    `UID:${opts.uid}`,
    `DTSTAMP:${icsStamp()}`,
    `DTSTART;VALUE=DATE:${opts.startIso}`,
    `DTEND;VALUE=DATE:${opts.endIso}`,
    `SUMMARY:${opts.summary}`,
    `DESCRIPTION:${opts.description}`,
    "TRANSP:TRANSPARENT",
    "BEGIN:VALARM",
    "ACTION:DISPLAY",
    `DESCRIPTION:${opts.alarmText}`,
    `TRIGGER:${opts.alarmTrigger}`,
    "END:VALARM",
    "END:VEVENT",
  ].join("\r\n");
}

function icsTimedEvent(opts: {
  uid: string;
  start: Date;
  end: Date;
  summary: string;
  description: string;
}): string {
  return [
    "BEGIN:VEVENT",
    `UID:${opts.uid}`,
    `DTSTAMP:${icsStamp()}`,
    `DTSTART:${icsStamp(opts.start)}`,
    `DTEND:${icsStamp(opts.end)}`,
    `SUMMARY:${opts.summary}`,
    `DESCRIPTION:${opts.description}`,
    "TRANSP:OPAQUE",
    "STATUS:CONFIRMED",
    "X-MICROSOFT-CDO-BUSYSTATUS:BUSY",
    "BEGIN:VALARM",
    "ACTION:DISPLAY",
    "DESCRIPTION:Lynx Study Session reminder",
    "TRIGGER:-PT30M",
    "END:VALARM",
    "END:VEVENT",
  ].join("\r\n");
}

export function buildStudyPlanIcs(opts: {
  certification: string;
  plan: PlanDayInput[];
  examDate?: string;
}): string {
  const title = opts.certification.trim() || "Certification";
  const slug = title.replace(/[^a-z0-9]+/gi, "-").slice(0, 24);
  const events = opts.plan
    .filter((day) => day.hours > 0)
    .map((day) => {
      const d = safeDate(day.date);
      if (!d) return "";
      const iso = toIso(d).replace(/-/g, "");
      const topics = day.sections.map((s) => `${s.name} (${s.hours}h)`).join(", ");
      const summary = icsEscape(`${title} — ${topics || "Lynx Study Session"}`);
      const description = icsEscape(
        `Study ${day.hours} hour${day.hours === 1 ? "" : "s"} for ${title}.\n${topics}\n\nGenerated on CertLynx.`
      );

      const start = combineLocalDateTime(day.date, day.startTime || "");
      let end = combineLocalDateTime(day.date, day.endTime || "");
      if (start && end && end.getTime() <= start.getTime()) {
        end = new Date(end.getTime());
        end.setDate(end.getDate() + 1);
      }

      if (start && end) {
        return icsTimedEvent({
          uid: `study-${iso}-${slug}@certlynx.com`,
          start,
          end,
          summary,
          description,
        });
      }

      return icsAllDayEvent({
        uid: `study-${iso}-${slug}@certlynx.com`,
        startIso: iso,
        endIso: toIso(addDays(d, 1)).replace(/-/g, ""),
        summary,
        description,
        alarmTrigger: "-P1D",
        alarmText: "Lynx Study Session reminder",
      });
    })
    .filter(Boolean);

  const exam = opts.examDate ? safeDate(opts.examDate) : null;
  if (exam) {
    const iso = toIso(exam).replace(/-/g, "");
    const next = toIso(addDays(exam, 1)).replace(/-/g, "");
    events.push(
      icsAllDayEvent({
        uid: `exam-${iso}-${slug}@certlynx.com`,
        startIso: iso,
        endIso: next,
        summary: icsEscape(`${title} exam`),
        description: icsEscape(`Exam day for ${title}. Generated on CertLynx.`),
        alarmTrigger: "-P1D",
        alarmText: "Exam reminder",
      })
    );
  }

  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//CertLynx//Study Planner//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    `X-WR-CALNAME:${icsEscape(`${title} study plan`)}`,
    events.join("\r\n"),
    "END:VCALENDAR",
  ].join("\r\n");
}

export function downloadStudyPlanIcs(opts: {
  certification: string;
  plan: PlanDayInput[];
  examDate?: string;
}): void {
  const ics = buildStudyPlanIcs(opts);
  trackGaEvent("calendar_invite_downloaded");
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "certlynx-study-plan.ics";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
